import { useId, useState, type ReactNode } from 'react'
import { Badge, Button, SegmentedControl, Surface } from '../controls'
import { StateBoundary, type ExperienceState } from './StateBoundary'
import s from './InsightCard.module.css'

export type InsightCardView = 'chart' | 'table' | 'narrative'

export interface InsightCardProps {
  title: ReactNode
  intent: ReactNode
  description?: ReactNode
  value?: ReactNode
  comparison?: ReactNode
  freshness?: { label: ReactNode; stale?: boolean }
  state?: ExperienceState
  /** Renderer output only. The card fetches nothing and knows no chart library. */
  visual: ReactNode
  /** An accessible representation of the same governed result. */
  table: ReactNode
  /** The meaningful conclusion, not merely a chart-type description. */
  narrative: ReactNode
  /** Name of the primary representation: Chart, List, Recommendation, etc. */
  visualLabel?: string
  defaultView?: InsightCardView
  action?: { label: string; onInvoke: () => void; disabled?: boolean }
  testId?: string
}

/**
 * The premium unit around every governed visual (#1188).
 *
 * It owns interpretation, freshness, fallbacks and the one permitted drill
 * action. The host owns data and authority; a renderer is only one of three
 * equivalent representations and can never become the access boundary.
 */
export function InsightCard({
  title, intent, description, value, comparison, freshness,
  state = { kind: 'ready' }, visual, table, narrative,
  visualLabel = 'Chart', defaultView = 'chart', action, testId,
}: InsightCardProps) {
  const [view, setView] = useState<InsightCardView>(defaultView)
  const headingId = useId()
  const viewOptions = [
    { value: 'chart' as const, label: visualLabel },
    { value: 'table' as const, label: 'Data table' },
    { value: 'narrative' as const, label: 'Explanation' },
  ]

  return (
    <Surface className={s.card} data-testid={testId} role="group" aria-labelledby={headingId}>
      <header className={s.header}>
        <div className={s.copy}>
          <div className={s.titleRow}>
            <h3 id={headingId}>{title}</h3>
            {freshness ? (
              <Badge tone={freshness.stale ? 'warn' : 'neutral'}>
                {freshness.label}
              </Badge>
            ) : null}
          </div>
          <p className={s.intent}>{intent}</p>
          {description != null ? <p className={s.description}>{description}</p> : null}
        </div>
        {value != null ? (
          <div className={s.metric} role="group" aria-label="Current insight value">
            <strong>{value}</strong>
            {comparison != null ? <span>{comparison}</span> : null}
          </div>
        ) : null}
      </header>

      <div className={s.toolbar}>
        <div className={s.views}>
          <SegmentedControl
            options={viewOptions}
            value={view}
            onChange={setView}
            label="Insight representation"
          />
        </div>
        {action ? (
          <Button onClick={action.onInvoke} disabled={action.disabled}>{action.label}</Button>
        ) : null}
      </div>

      <StateBoundary state={state} testId={testId ? `${testId}-state` : undefined}>
        <div className={s.content} data-view={view} aria-live="polite">
          {view === 'chart' ? (
            <figure className={s.visual}>
              {visual}
              <figcaption className={s.srOnly}>{narrative}</figcaption>
            </figure>
          ) : null}
          {view === 'table' ? <div className={s.table}>{table}</div> : null}
          {view === 'narrative' ? <div className={s.narrative}>{narrative}</div> : null}
        </div>
      </StateBoundary>
    </Surface>
  )
}
